import React, { useState, useMemo } from "react";
import StatWrapper from "../shared/StatWrapper";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import Button from "hardcover-ui/components/Button";
import QuestionMarkCircleIcon from "icons/solid/circle-question.svg";
import Icon from "hardcover-ui/components/Icon";

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "hardcover-ui/components/Tooltip";

import { useChartTheme } from "../contexts/ChartThemeContext";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import { usePage } from "@inertiajs/react";

interface GenreBreakdownStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const GenreBreakdownStat: React.FC<GenreBreakdownStatProps> = ({ size }) => {
  const { isOwner, user, stats } = usePage<UserStatsPageProps>().props;
  const { genresBreakdown: data } = stats;
  const { theme } = useChartTheme();
  const [showAll, setShowAll] = useState(false);

  if (!data.genres || data.genres.length === 0) {
    return (
      <StatWrapper title="Genre Breakdown" size={size}>
        <Heading size="sm" className="mb-4">
          No genre data available
        </Heading>
      </StatWrapper>
    );
  }

  const genres = useMemo(
    () =>
      data.genres.map((genre, index) => ({
        ...genre,
        color: `var(${
          theme === "colorful" ? "--chart-colorful-" : "--chart-mono-"
        }${(index % 10) + 1})`,
      })),
    [theme]
  );

  const displayedGenres = showAll ? genres : genres.slice(0, 8);
  const totalBooks = useMemo(
    () => genres.reduce((sum, genre) => sum + (genre.value || 0), 0),
    [genres]
  );
  const maxCount = useMemo(
    () => Math.max(...genres.map((g) => g.value || 0)),
    [genres]
  );

  return (
    <StatWrapper title="Genre Breakdown" size={size}>
      <div className="flex flex-col h-full w-full">
        <Heading size="sm" className="mb-4">
          {displayedGenres[0]?.label || "No genre"} is{" "}
          {isOwner ? "your" : `${user.name}'s`} top genre
        </Heading>
        <div className="space-y-2 flex-grow">
          {displayedGenres.map((genre) => {
            const percentage =
              totalBooks > 0 ? ((genre.value || 0) / totalBooks) * 100 : 0;
            const barWidth =
              maxCount > 0 ? ((genre.value || 0) / maxCount) * 100 : 0;

            return (
              <div key={genre.label} className="flex items-center">
                <Text className="w-24" style={{ color: genre.color }}>
                  {genre.label}
                </Text>
                <div className="flex-grow dark:bg-gray-700 bg-gray-100 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: genre.color,
                    }}
                  />
                </div>
                <Text
                  className="ml-2 w-32 text-right"
                  style={{ color: genre.color }}
                >
                  {genre.value || 0} ({percentage.toFixed(1)}%)
                </Text>
              </div>
            );
          })}
        </div>
        {!showAll && genres.length > 8 && (
          <Button
            onClick={() => setShowAll(true)}
            className="mt-4"
            size="sm"
            variant="tertiary"
          >
            Load More
          </Button>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mt-4 flex items-center text-tertiary cursor-help">
              <Icon
                icon={QuestionMarkCircleIcon}
                className="mr-1 w-5 h-5 text-tertiary"
              />
              <span className="text-sm">
                Missing data from {data.missingDataCount} books
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>These books don't have genre information.</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </StatWrapper>
  );
};

export default GenreBreakdownStat;
